import {
	type ApiErrorResponse,
	type Country,
	type FanShopCheckoutOrder,
	type FanShopCheckoutOrderFieldCategory,
	Logger,
	queryClient
} from '@capsicum/common';
import { type AxiosError } from 'axios';
import { useAtom } from 'jotai';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useState } from 'react';

import { placeOrderPayloadAtom, UseProductQueryKeyBuilder } from '../../data-access';
import { type DocumentType, type FieldsObject } from '../../models/orders';
import { useProductCheckout } from '../useCheckout';

type UseProductOrderProps = {
	variantId: string;
};

export const useProductOrder = ({ variantId }: UseProductOrderProps) => {
	const [placeOrderDetails] = useAtom(placeOrderPayloadAtom);
	const [orderPlaced, setOrderPlaced] = useState<boolean>(false);
	const [fieldsWithEmptyValuesError, setFieldsWithEmptyValuesError] = useState<boolean>(false);
	const { completeCheckoutData, mutatePlaceOrder } = useProductCheckout();

	const preparePlaceOrder = (fields: FieldsObject): FanShopCheckoutOrder['deliveryDetails'] => {
		// We need to deep clone it as it has nested objects and we need to avoid mutating the original object
		const preparedFields = cloneDeep(fields);

		for (const key in preparedFields) {
			Object.values(preparedFields[key]!).forEach(detail => {
				switch (detail?.fieldType) {
					case 'list':
						if (detail?.options?.isCountryList) {
							if (typeof detail.value !== 'string') {
								detail.value = (detail.value as Country)?.iso2 ?? null;
							}
						}
						if (detail?.options?.isDocumentTypeList) {
							if (typeof detail.value !== 'string') {
								detail.value = (detail.value as DocumentType)?.name ?? null;
							}
						}
						break;
					default:
						break;
				}
			});
		}

		return preparedFields;
	};

	const getFieldsWithEmptyValues = (
		deliveryDetails: FanShopCheckoutOrder['deliveryDetails']
	): Array<FanShopCheckoutOrderFieldCategory> => {
		if (!deliveryDetails) {
			return [];
		}

		return Object.values(deliveryDetails)
			.filter(fields => Array.isArray(fields) && fields.length > 0)
			.flatMap(fields =>
				fields
					.filter(field => field?.value === null || field?.value === undefined)
					.map(field => field?.fieldCategory)
					.filter((category): category is FanShopCheckoutOrderFieldCategory => !!category)
			);
	};

	const sendPlaceOrderErrorToSentry = useCallback(
		(
			error: AxiosError<ApiErrorResponse, unknown> | Error,
			fieldsWithInvalidValues: Array<FanShopCheckoutOrderFieldCategory>
		) => {
			Logger.error(`Error placing order on fan-shop-service/order: ${error?.message}`, {
				errorTitle:
					(error as AxiosError<ApiErrorResponse, unknown>)?.response?.data?.errors?.[0]?.title ||
					error?.message,
				fieldsWithInvalidValues: JSON.stringify(fieldsWithInvalidValues),

				variantId
			});
		},
		[variantId]
	);

	const placeOrder = useCallback(async () => {
		setOrderPlaced(true);

		const preparedDeliveryDetails = preparePlaceOrder(placeOrderDetails.deliveryDetails as FieldsObject);

		const fieldsWithInvalidValues = getFieldsWithEmptyValues(preparedDeliveryDetails);

		if (fieldsWithInvalidValues.length > 0) {
			Logger.info('Fields with null or undefined values detected', {
				fieldsWithInvalidValues
			});

			setFieldsWithEmptyValuesError(true);

			return;
		}

		await mutatePlaceOrder(
			{
				deliveryDetails: preparedDeliveryDetails,
				plannedOrder: placeOrderDetails.plannedOrder
			},
			{
				onError: error => {
					sendPlaceOrderErrorToSentry(error, fieldsWithInvalidValues);
				},
				onSettled: () => {
					queryClient.invalidateQueries({ queryKey: UseProductQueryKeyBuilder(variantId) });
				}
			}
		);
	}, [
		variantId,
		mutatePlaceOrder,
		placeOrderDetails.deliveryDetails,
		preparePlaceOrder,
		sendPlaceOrderErrorToSentry
	]);

	return {
		completeCheckoutData,
		fieldsWithEmptyValuesError,
		getFieldsWithEmptyValues,
		orderPlaced,
		placeOrder,
		placeOrderDetails,
		preparePlaceOrder,
		setFieldsWithEmptyValuesError,
		setOrderPlaced
	};
};
